import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {usePins} from './usePins';
import {parseScopeParam, scopeKeyOf, type ScopeItem} from '../lib/scope-utils';

export type LibraryPage = {
  id: string;
  name: string;
  items: ScopeItem[];
};

/**
 * Saved Library "Pages" (named pins) plus the currently-active one, derived
 * from the URL. Shared by every sidebar surface that lists pages — the
 * expanded rail (`LibraryRail`) and the collapsed-sidebar flyout
 * (`CollapsedPagesFlyout`) — so the pin→page shape and the "which page is
 * the user on" derivation live in exactly one place.
 */
export function useLibraryPages(): {
  pages: LibraryPage[];
  activeScopeKey: string;
} {
  const {pathname, search} = useLocation();
  const {data: pins} = usePins();

  const onLibrary = pathname === '/library' || pathname.startsWith('/library/');
  const params = new URLSearchParams(search);
  const activeScopeKey =
    onLibrary && params.get('scope')
      ? scopeKeyOf(parseScopeParam(params.get('scope') ?? ''))
      : '';

  const pages = useMemo(
    () =>
      (pins ?? []).map((pin) => ({
        id: pin.id,
        name: pin.label,
        items: pin.payload.refs.map((ref) => ({
          typeId: ref.objectTypeId,
          recordId: ref.objectRecordId,
        })),
      })),
    [pins]
  );

  return {pages, activeScopeKey};
}
